import React, { useEffect, useCallback, useMemo } from 'react';

import { useHistory } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import formatMoney from '@julofinance/web-helpers/dist/string/formatMoney';

import Page from 'components/Page';
import Button from 'components/Form/Button';

import useGlobalState from 'actions';
import { useRGetAgreementSummary } from '@repositories/agreement';
import { useProfile } from '@hooks/use-profile';

import { centerAlignOfferDiv } from 'pages/Homepage/styles';

import juloLogo from 'assets/logo_julo.png';
import {
  summaryHeading,
  textLeft,
  textRight,
  txtSummary,
  txtSummaryLeft,
  txtSummaryRight,
} from './styles';

const AgreementSummaryPage = () => {
  const [globalState, globalActions] = useGlobalState();
  const history = useHistory();

  const { data: profile } = useProfile();
  const loanXid = profile?.loan?.loanXid;

  const { data } = useRGetAgreementSummary({
    loanXid,
    enabled: !!loanXid,
  });
  const agreementSummaryData = useMemo(
    () => data?.data || { loan: {}, bank: {} },
    [data?.data],
  );
  const loanSummary = useMemo(
    () => agreementSummaryData.loan || {},
    [agreementSummaryData],
  );
  const bankSummary = useMemo(
    () => agreementSummaryData.bank || {},
    [agreementSummaryData],
  );

  const showErrorNotification = useCallback(
    (msg: string) => {
      globalActions.openNotification(msg);
      setTimeout(() => {
        globalActions.closeNotification();
      }, globalState.notificationDelayTime);
    },
    [globalActions, globalState.notificationDelayTime],
  );

  useEffect(() => {
    if (!loanXid) {
      showErrorNotification(globalState.noLoanXidError);
    }
  }, [globalState.noLoanXidError, loanXid, showErrorNotification]);

  const amount = formatMoney(loanSummary.amount || 0);
  const upfrontFee = formatMoney(loanSummary.upfrontFee || 0);
  const disbursalAmount = formatMoney(loanSummary.disbursalAmount || 0);
  const dailyRepayment = formatMoney(loanSummary.dailyRepayment || 0);
  const repayIn = loanSummary.tenure;
  const interestRate = loanSummary.interestRate;

  return (
    <>
      <Helmet>
        <title>JULO - Ringkasan Perjanjian</title>
      </Helmet>
      <Page useHeader={true}>
        <div className={centerAlignOfferDiv}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm container>
              <Grid item xs container direction='column' spacing={2}>
                <Grid item xs>
                  <div>
                    <img src={juloLogo} alt='logo' />
                  </div>
                </Grid>
              </Grid>
              <Grid item>
                <div className={summaryHeading}>Ringkasan Perjanjian</div>
              </Grid>
            </Grid>
          </Grid>
          <Paper style={{ marginTop: '24px' }}>
            <Grid container spacing={1}>
              <Grid item xs={12} container>
                <Grid item xs container direction='column'>
                  <Grid item>
                    <div className={txtSummaryLeft}>Jumlah pinjaman</div>
                    <div className={txtSummaryLeft}>Biaya admin</div>
                    <div className={txtSummaryLeft}>Dana cair</div>
                    <div className={txtSummaryLeft}>Cicilan per hari</div>
                    <div className={txtSummaryLeft}>Jangka waktu</div>
                    <div className={txtSummaryLeft}>Bunga per bulan</div>
                  </Grid>
                </Grid>
                <Grid item>
                  <div className={txtSummaryRight}>{amount}</div>
                  <div className={txtSummaryRight}>{upfrontFee}</div>
                  <div className={txtSummaryRight}>{disbursalAmount}</div>
                  <div className={txtSummaryRight}>{dailyRepayment}</div>
                  <div className={txtSummaryRight}>{repayIn} days</div>
                  <div className={txtSummaryRight}>{interestRate}%</div>
                </Grid>
              </Grid>
            </Grid>
          </Paper>
          <p className={txtSummary}>Recipient&apos;s Bank Account</p>
          <Paper style={{ marginTop: '16px' }}>
            <Grid container direction='column' padding={1}>
              <Grid item container justifyContent='space-between'>
                <span className={textLeft}>Nama bank</span>
                <span className={textRight}>{bankSummary.bankName}</span>
              </Grid>
              <Grid item container justifyContent='space-between'>
                <span className={textLeft}>Atas nama</span>
                <span className={textRight}>{bankSummary.accountName}</span>
              </Grid>
              <Grid item container justifyContent='space-between'>
                <span className={textLeft}>Nomor Rekening</span>
                <span className={textRight}>{bankSummary.accountNumber}</span>
              </Grid>
            </Grid>
          </Paper>
          <div className='grab-footer'>
            <div style={{ padding: '16px 0' }}>
              <Button
                onClick={() => {
                  localStorage.setItem('redirectPage', '/agreement_summary');
                  history.push('/agreement_letter');
                }}
              >
                Lihat SKRTP
              </Button>
            </div>
          </div>
        </div>
      </Page>
    </>
  );
};

export default AgreementSummaryPage;
